type CacheEntry<T> = {
  value: T;
  expireAt: number | null;
};

/**
 * 基于惰性过期（Lazy Expiration）和 LRU 淘汰的内存缓存类。
 * - 过期检查仅在访问时进行（get/has/size）
 * - 自动按 LRU 策略淘汰超容数据
 * - 支持泛型、TTL、容量限制
 */
export default class LzdTTLCache<T = any> {
  private cache: Map<string, CacheEntry<T>>;
  private maxSize: number;
  private defaultTTL: number; // 默认 TTL（毫秒），<=0 表示永不过期

  /**
   * 基于惰性过期（Lazy Expiration）和 LRU 淘汰的内存缓存类。
   * - 过期检查仅在访问时进行（get/has/size）
   * - 自动按 LRU 策略淘汰超容数据
   * - 支持泛型、TTL、容量限制
   * @param maxSize 最大缓存数量，默认 100
   * @param defaultTTL 默认过期时间（毫秒），<=0 表示永不过期，默认 30s
   */
  constructor(maxSize?: number, defaultTTL?: number) {
    this.maxSize = Math.max(1, maxSize ?? 100);
    this.defaultTTL = defaultTTL ?? 30_000;
    this.cache = new Map<string, CacheEntry<T>>();
  }

  /**
   * 设置缓存项
   */
  set(key: string, value: T, ttl: number = this.defaultTTL) {
    // 计算过期时间
    const expireAt = ttl > 0 ? Date.now() + ttl : null;

    // 如果已存在，先删除（保证 LRU 顺序正确）
    if (this.cache.has(key)) {
      this.cache.delete(key);
    }

    // 插入到末尾（最新使用）
    this.cache.set(key, { value, expireAt });

    // 超出容量？删除最旧的（Map 的第一个元素）
    if (this.cache.size > this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey !== void 0) {
        this.cache.delete(firstKey);
      }
    }
  }

  /**
   * 获取缓存项（访问时自动检查过期）
   */
  get(key: string) {
    const entry = this.cache.get(key);
    if (!entry) return void 0;

    // 检查是否过期
    if (entry.expireAt !== null && Date.now() > entry.expireAt) {
      this.cache.delete(key);
      return void 0;
    }

    // LRU：移到最后（标记为最近使用）
    this.moveToEnd(key);

    return entry.value;
  }

  /**
   * 检查是否存在且未过期
   */
  has(key: string) {
    return this.get(key) !== void 0;
  }

  /**
   * 删除指定缓存项
   */
  delete(key: string) {
    return this.cache.delete(key);
  }

  /**
   * 清空所有缓存
   */
  clear() {
    this.cache.clear();
  }

  /**
   * 手动清理所有已过期项（可选调用）
   */
  clearExpired() {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (entry.expireAt !== null && now > entry.expireAt) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * 获取当前有效（未过期）缓存数量
   * ⚠️ 注意：此操作是 O(n)，避免高频调用
   */
  get size() {
    let count = 0;
    const now = Date.now();
    for (const entry of this.cache.values()) {
      if (entry.expireAt === null || now <= entry.expireAt) {
        count++;
      }
    }
    return count;
  }

  /**
   * 获取缓存总容量（包含已过期但未清理的项）
   * 性能 O(1)
   */
  get totalSize() {
    return this.cache.size;
  }

  /**
   * 将 key 对应的项移到 Map 末尾（模拟 LRU 最近使用）
   */
  private moveToEnd(key: string) {
    const entry = this.cache.get(key);
    if (entry) {
      this.cache.delete(key);
      this.cache.set(key, entry);
    }
  }
}
